<template>
  <tr>
    <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-0 space-y-1">
      <router-link :to="`/customer/${customerId}`" class="font-bold text-brand-black overflow-ellipsis whitespace-nowrap overflow-hidden">
        <span class="confidential-blurry-text">{{ customerEmail | truncate(24) }}</span>
      </router-link>
      <ul class="flex items-center text-sm font-semibold text-brand-brown overflow-ellipsis space-x-2">
        <span :style="{ color: riskGroup.color }">
          {{ riskGroup.label }}
        </span>
        <span v-if="sourceCountryFlag">
          <div class="" style="width: 18px; height: 12px">
            <img :src="sourceCountryFlag" class="w-full h-full object-cover shadow-sm" :alt="sourceCountry" />
          </div>
        </span>
      </ul>
    </td>
    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-700 font-semibold w-52 text-center">
      <span
        class="inline-flex capitalize w-20 items-center px-2.5 py-1 rounded-lg text-xs font-semibold justify-center"
        :class="subscriptionStatusClass"
      >
        {{ subscriptionStatusLabel }}
      </span>
    </td>
    <td class="whitespace-nowrap px-8 py-3 text-sm text-brand-brown font-semibold text-center space-y-2">
      <p class="font-semibold confidential-blurry-text">{{ subscriptionPlanPrice }}</p>
      <p class="text-xs confidential-blurry-text">
        {{ subscriptionName | truncate(30) }}
      </p>
    </td>
    <td class="whitespace-nowrap px-8 py-3 text-sm text-brand-brown font-semibold text-center space-y-2">
      <p class="font-semibold confidential-blurry-text">
        {{ invoiceAmount }}
      </p>
      <p class="text-xs capitalize">{{ invoiceDescription }}</p>
    </td>
    <td class="whitespace-nowrap px-8 pt-0 pb-1.5 text-sm text-brand-brown font-semibold text-center space-y-1" v-if="source">
      <div v-if="sourceImage" class="w-full">
        <img :src="sourceImage" class="w-10 h-8 mx-auto" />
      </div>
      <p class="text-xs">
        <span v-if="sourceFunding" class="capitalize">
          {{ sourceFunding }}
        </span>
        <span v-if="sourceExpiration"> exp. {{ sourceExpiration | shortMonthYearLabel }} </span>
      </p>
    </td>
    <td v-else class="whitespace-nowrap px-8 py-3 text-sm text-brand-brown font-semibold text-center space-y-2">
      <p>-</p>
    </td>
    <td class="whitespace-nowrap px-3 py-1 text-sm text-gray-700 font-semibold w-48">
      <VueVegaEmbed
        :spec="
          genSparklineSpec({
            data: row.observations.map((o, i) => ({
              index: i,
              day: new Date(o.timestamp),
              score: o.emaFast,
              customerEmail: customerEmail,
            })),
            xField: 'day',
            yField: 'score',
            yMax: 10,
            yMin: 0,
            color: riskGroup.color,
            xFieldType: 'temporal',
          })
        "
        width="auto"
        :height="60"
      />
    </td>
  </tr>
</template>

<script>
import { formatIntlCurrency, formatIntlPlan } from '@/helpers/currency-utils';
import { isoCountries } from '@/helpers/iso3166-countries';
import { cardBrandImg, riskGroups } from '@/store/shared';
import VueVegaEmbed from '@/components/Vis/Vega/VueVegaEmbed.vue';
import { genSparklineSpec } from '@/components/Vis/Vega/Specs/SparklineSpec';
import { mapGetters } from 'vuex';

export default {
  name: 'HealthWatchlistRow',
  components: {
    VueVegaEmbed,
  },
  props: {
    row: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters(['confidential']),
    invoice() {
      return this.row.latestInvoice;
    },
    subscription() {
      return this.row.subscription;
    },
    customer() {
      return this.row.customer;
    },
    source() {
      return this.customer?.source;
    },
    riskGroup() {
      return riskGroups.find((g) => g.key === this.row.latestObservation.riskCategory);
    },
    customerId() {
      return this.customer.customerId;
    },
    customerEmail() {
      return this.customer ? this.customer.email : 'N/A';
    },
    subscriptionStatus() {
      return this.subscription?.status || 'N/A';
    },
    subscriptionStatusClass() {
      return (
        {
          active: 'text-green-700 bg-green-200',
          past_due: 'text-yellow-700 bg-yellow-200',
          unpaid: 'text-red-700 bg-red-200',
          canceled: 'text-brand-brown bg-brand-light-sand',
          incomplete: 'text-blue-700 bg-blue-200',
          incomplete_expired: 'text-purple-700 bg-purple-200',
          trialing: 'text-indigo-700 bg-indigo-200',
        }[this.subscriptionStatus] || 'text-gray-700 bg-gray-200'
      );
    },
    subscriptionStatusLabel() {
      return (
        {
          active: 'Active',
          past_due: 'Past Due',
          unpaid: 'Unpaid',
          canceled: 'Cancelled',
          incomplete: 'Incomplete',
          incomplete_expired: 'Incomplete Expired',
          trialing: 'Trialing',
        }[this.subscriptionStatus] || 'N/A'
      );
    },
    subscriptionPlanPrice() {
      const currency = this.subscription.currency || 'usd';
      const items = this.subscription.subscription_items;
      if (items?.length) {
        const first = items[0];
        const price = first.price;
        return formatIntlPlan(price.amount, currency, price.interval, price.intervalCount);
      }
      if (this.subscription.plan?.amount) {
        return formatIntlPlan(
          this.subscription.plan.amount,
          currency,
          this.subscription.plan.interval || 'month',
          this.subscription.plan.intervalCount || 1
        );
      }
      return '-';
    },
    subscriptionName() {
      return this.subscription.plan?.nickname || this.subscription.plan?.name || this.subscription.plan?.id;
    },
    subscriptionStartDate() {
      return new Date(this.subscription.created).toLocaleDateString();
    },
    invoiceAmount() {
      return formatIntlCurrency(this.invoice.amountDue, this.invoice.currency);
    },
    invoiceDate() {
      return new Date(this.invoice.created).toLocaleDateString();
    },
    invoiceDescription() {
      if (this.invoice.status === 'paid') {
        return `Paid ${this.invoiceDate}`;
      }
      if (this.invoice.status === 'open') {
        return `Due ${this.invoiceDate}`;
      }
      return `${this.invoice.status}`;
    },
    sourceCountry() {
      if (this.source?.country) {
        const match = isoCountries.find((c) => c.alpha2 === this.source.country);
        if (match) {
          if (match.country.length > 20) {
            return match.alpha3;
          }
          return match.country;
        }
      }
      return '-';
    },
    sourceCountryFlag() {
      if (this.source?.country) {
        const match = isoCountries.find((c) => c.alpha2 === this.source.country);
        if (match) {
          return `https://flagcdn.com/w80/${match.alpha2.toLowerCase()}.png`;
        }
      }
      return '';
    },
    sourceBrand() {
      return this.source?.brand || '';
    },
    sourceImage() {
      return cardBrandImg[this.sourceBrand?.toLowerCase()] || '';
    },
    sourceFunding() {
      return this.customer?.source?.funding || '';
    },
    sourceExpiration() {
      return this.source?.expiration ? new Date(this.source.expiration) : null;
    },
  },
  methods: {
    genSparklineSpec,
  },
};
</script>
